import React, { useState, useMemo } from "react";
import apiClient from "../../api/axios";
import { useAllData } from "../../contexts/AllDataContext";
import { useExportData } from "./useExportData";
import { useExportSelection } from "./useExportSelection";
import { buildCsvFileName, saveBlobAsFile, formatCsvRows } from "../../utils/csvExport";

export function useExportPage() {
  const allDataCtx = useAllData();
  const {
    itemsPerPage,
    setItemsPerPage,
    currentPage,
    totalPages,
    fetchNextPage,
    fetchPrevPage,
    goToFirstPage,
    hasNextPage,
    hasPrevPage,
    sortOrder,
    setSortOrder,
    dataMode,
    pagination,
  } = allDataCtx;

  const [format, setFormat] = useState("itandi");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isDownloading, setIsDownloading] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);

  const exportData = useExportData(format, allDataCtx);

  const currentVisibleRows = useMemo(
    () => exportData.results.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [exportData.results, page, rowsPerPage]
  );

  const selection = useExportSelection(
    exportData.results,
    currentVisibleRows,
    allDataCtx.dataMode
  );

  const totalCount =
    allDataCtx.dataMode === 'paginated' && pagination?.totalCount != null
      ? pagination.totalCount
      : exportData.results.length;
  const rowCount = totalCount;

  const handleChangePage = async (event: unknown, newPage: number) => {
    // Path B（paginated）: 表示に必要な件数がロード済みデータに足りなければ追加取得
    if (dataMode === 'paginated') {
      const neededCount = (newPage + 1) * rowsPerPage;
      if (allDataCtx.contracts.length < neededCount && hasNextPage) {
        const fetchCount = Math.ceil((neededCount - allDataCtx.contracts.length) / itemsPerPage);
        for (let i = 0; i < fetchCount; i++) {
          await fetchNextPage();
        }
      }
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);

    // Path B: 表示件数を増やしたとき、1ページ目を表示するのに必要なデータが足りなければ取得
    if (dataMode === 'paginated' && allDataCtx.contracts.length < newRowsPerPage && hasNextPage) {
      const fetchCount = Math.ceil((newRowsPerPage - allDataCtx.contracts.length) / itemsPerPage);
      for (let i = 0; i < fetchCount; i++) {
        await fetchNextPage();
      }
    }
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    setClientError(null);
    try {
      const suggestedName = buildCsvFileName(format);

      if (selection.selectAllMode) {
        // Path B 全件: バックエンドに全件CSV生成を依頼
        const response = await apiClient.post(
          "/api/download-csv-all",
          {
            format,
            columns: exportData.columnDefs,
            filters: {
              propertyFilter: exportData.propertyFilter,
              roomFilter: exportData.roomFilter,
              lesseeFilter: exportData.lesseeFilter,
            },
          },
          { responseType: "blob" }
        );
        await saveBlobAsFile(new Blob([response.data], { type: "text/csv" }), suggestedName);
        return;
      }

      if (selection.selectedIds.size === 0) {
        setClientError("エクスポート対象のデータが選択されていません。");
        return;
      }

      const dataToExport = exportData.allData.filter((c: any) => selection.selectedIds.has(c.id!));
      const rows = formatCsvRows(dataToExport, exportData.columnDefs, format);
      const response = await apiClient.post(
        "/api/download-csv",
        { columns: exportData.columnDefs, data: rows },
        { responseType: "blob" }
      );
      await saveBlobAsFile(new Blob([response.data], { type: "text/csv" }), suggestedName);
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('File save dialog cancelled by user.');
        return;
      }
      setClientError("CSVのダウンロード中にエラーが発生しました。");
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    // format
    format,
    setFormat,
    // data (useExportData)
    ...exportData,
    // selection (useExportSelection)
    ...selection,
    // pagination (ExportPage独自)
    page,
    rowsPerPage,
    currentVisibleRows,
    totalCount,
    rowCount,
    handleChangePage,
    handleChangeRowsPerPage,
    // download
    isDownloading,
    clientError,
    handleDownload,
    // AllDataContext ページネーション
    itemsPerPage,
    setItemsPerPage,
    currentPage,
    totalPages,
    fetchNextPage,
    fetchPrevPage,
    goToFirstPage,
    hasNextPage,
    hasPrevPage,
    sortOrder,
    setSortOrder: (newOrder: 'asc' | 'desc') => { setPage(0); setSortOrder(newOrder); },
    dataMode,
    pagination,
  };
}
